import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './WelcomePage.css';
import logo from '../../assets/mobiRoute.png';
import foresLogo from '../../assets/fores-logo.png';

const WelcomePage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'web' | 'mobile'>('web');
    const [activeFaq, setActiveFaq] = useState<number | null>(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const sectionsRef = useRef<HTMLDivElement>(null);

    // Scroll listener for header effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Smooth scroll to section
    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleSignIn = () => {
        sessionStorage.setItem('welcomeShown', 'true');
        navigate('/login');
    };

    const toggleFaq = (index: number) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    const features = [
        {
            icon: 'dx-icon-map',
            title: 'Rota Optimizasyonu',
            description: 'Gelişmiş algoritmamızla seyahat süresini ve maliyetleri azaltan verimli rota planlaması.',
            color: 'blue'
        },
        {
            icon: 'dx-icon-globe',
            title: 'Gerçek Zamanlı Takip',
            description: 'Filonuzun her hareketini anlık olarak izleyin ve tahmini varış sürelerini görüntüleyin.',
            color: 'green'
        },
        {
            icon: 'dx-icon-group',
            title: 'Kolay Kullanım',
            description: 'Kullanıcı dostu mobil uygulamamızla sürücü eğitimini hızlandırın.',
            color: 'purple'
        },
        {
            icon: 'dx-icon-message',
            title: 'Müşteri Bildirimleri',
            description: 'Markanıza özel mesajlarla müşterilerinizi teslimat hakkında bilgilendirin.',
            color: 'orange'
        },
        {
            icon: 'dx-icon-chart',
            title: 'Detaylı Raporlar',
            description: 'Veriye dayalı kararlar için kapsamlı analitik ve raporlama araçları.',
            color: 'teal'
        },
        {
            icon: 'dx-icon-todo',
            title: 'Otomatik Atama',
            description: 'Tek tıkla teslimatları sürücülere otomatik olarak dağıtın.',
            color: 'pink'
        }
    ];

    const testimonials = [
        {
            name: 'Ahmet Y.',
            role: 'Filo Yöneticisi',
            company: 'ABC Lojistik',
            text: 'MobiRoute sayesinde günlük teslimat sayımızı %40 artırdık. Rota optimizasyonu gerçekten işe yarıyor.',
            avatar: 'AY'
        },
        {
            name: 'Mehmet K.',
            role: 'Operasyon Müdürü',
            company: 'XYZ Kargo',
            text: 'Sürücülerimiz artık daha az yakıt harcıyor ve daha fazla teslimat yapıyor. Mükemmel bir çözüm!',
            avatar: 'MK'
        },
        {
            name: 'Fatma S.',
            role: 'Kurye',
            company: 'Hızlı Teslimat',
            text: 'Uygulama çok kullanışlı. Rotalarımı hızlıca planlayabiliyorum ve müşterilerime zamanında ulaşıyorum.',
            avatar: 'FS'
        }
    ];

    const faqs = [
        {
            question: 'MobiRoute nasıl çalışır?',
            answer: 'MobiRoute, gelişmiş algoritmalar kullanarak teslimat noktalarınızı en verimli şekilde sıralar ve size optimal rotayı sunar. Sadece adreslerinizi girin, gerisini biz halledelim.'
        },
        {
            question: 'Kaç teslimat noktası ekleyebilirim?',
            answer: 'MobiRoute ile sınırsız teslimat noktası ekleyebilirsiniz. Günlük yüzlerce teslimatı kolayca yönetebilirsiniz.'
        },
        {
            question: 'Gerçek zamanlı takip nasıl çalışır?',
            answer: 'Sürücülerinizin konumlarını harita üzerinde anlık olarak görebilir, tahmini varış sürelerini takip edebilir ve müşterilerinize otomatik bildirim gönderebilirsiniz.'
        },
        {
            question: 'ERP sistemimle entegre edebilir miyim?',
            answer: 'Evet! MobiRoute, Fores ERP ve diğer popüler ERP/WMS sistemleriyle sorunsuz entegrasyon sağlar. API desteğimiz sayesinde özel entegrasyonlar da mümkündür.'
        },
        {
            question: 'Mobil uygulama mevcut mu?',
            answer: 'Evet, MobiRoute hem iOS hem de Android platformlarında ücretsiz olarak kullanılabilir. Sürücüleriniz mobil uygulama ile rotalarını takip edebilir.'
        }
    ];

    return (
        <div className="welcome-page" ref={sectionsRef}>
            {/* Header */}
            <header className={`welcome-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="header-logo">
                    <img src={logo} alt="MobiRoute" className="header-logo-img" />
                </div>
                <nav className="header-nav">
                    <button onClick={() => scrollToSection('features')} className="nav-link">Özellikler</button>
                    <button onClick={() => scrollToSection('products')} className="nav-link">Ürünler</button>
                    <button onClick={() => scrollToSection('testimonials')} className="nav-link">Referanslar</button>
                    <button onClick={() => scrollToSection('faq')} className="nav-link">SSS</button>
                </nav>
                <div className="header-actions">
                    <button className="sign-in-button" onClick={handleSignIn}>
                        <i className="dx-icon-login"></i>
                        Giriş Yap
                    </button>
                </div>
            </header>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-background">
                    <img
                        src="/assets/vrp.jpg"
                        alt="Background"
                        className="hero-bg-img"
                    />
                    <div className="hero-overlay"></div>
                </div>

                {/* Decorative Elements */}
                <div className="hero-decorations">
                    <div className="decoration-circle circle-1"></div>
                    <div className="decoration-circle circle-2"></div>
                    <div className="decoration-circle circle-3"></div>
                    <div className="decoration-line line-1"></div>
                    <div className="decoration-line line-2"></div>
                </div>

                <div className="hero-content">
                    <span className="hero-badge">
                        <i className="dx-icon-car"></i>
                        Kurumsal Lojistik Çözümü
                    </span>
                    <h1 className="hero-title">
                        Rotalarınızı Verimli Planlayın,<br />
                        <span className="gradient-text">Teslimatlarınızı Dönüştürün</span>
                    </h1>
                    <p className="hero-description">
                        MobiRoute ile filo yönetimi, rota optimizasyonu ve saha ekibi takibini
                        tek platformda yönetin. Zaman ve yakıt tasarrufu sağlayın.
                    </p>
                    <div className="hero-actions">
                        <button className="btn-primary" onClick={handleSignIn}>
                            <span>Hemen Başlayın</span>
                            <i className="dx-icon-arrowright"></i>
                        </button>
                        <button className="btn-outline" onClick={() => scrollToSection('features')}>
                            <span>Keşfedin</span>
                            <i className="dx-icon-chevrondown"></i>
                        </button>
                    </div>
                    <div className="hero-stats">
                        <div className="hero-stat">
                            <span className="stat-value">10,000+</span>
                            <span className="stat-label">Aktif Kullanıcı</span>
                        </div>
                        <div className="hero-stat">
                            <span className="stat-value">%40</span>
                            <span className="stat-label">Yakıt Tasarrufu</span>
                        </div>
                        <div className="hero-stat">
                            <span className="stat-value">150+</span>
                            <span className="stat-label">Kurumsal Müşteri</span>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="scroll-indicator" onClick={() => scrollToSection('trust')}>
                    <div className="scroll-mouse">
                        <div className="scroll-wheel"></div>
                    </div>
                    <span>Aşağı Kaydır</span>
                </div>
            </section>

            {/* Trust Badges */}
            <section id="trust" className="trust-section">
                <p className="trust-text">Avrupa'nın önde gelen şirketleri MobiRoute'a güveniyor</p>
                <div className="trust-logos">
                    <div className="trust-logo">
                        <i className="dx-icon-box"></i>
                        <span>Demka</span>
                    </div>
                    <div className="trust-logo">
                        <i className="dx-icon-food"></i>
                        <span>Alba Food</span>
                    </div>
                    <div className="trust-logo">
                        <i className="dx-icon-home"></i>
                        <span>Ak-Pa Handels</span>
                    </div>
                    <div className="trust-logo">
                        <i className="dx-icon-product"></i>
                        <span>Elbey GmbH</span>
                    </div>
                </div>
            </section>

            {/* Capabilities Section - Replaces Image Showcase */}
            <section className="capabilities-section">
                <div className="capabilities-grid">
                    <div className="capability-card">
                        <div className="capability-icon">
                            <i className="dx-icon-car"></i>
                        </div>
                        <h3>Filo Yönetimi</h3>
                        <p>Tüm araçlarınızı tek panelden yönetin</p>
                    </div>
                    <div className="capability-card">
                        <div className="capability-icon">
                            <i className="dx-icon-map"></i>
                        </div>
                        <h3>Rota Planlama</h3>
                        <p>En verimli rotaları otomatik oluşturun</p>
                    </div>
                    <div className="capability-card">
                        <div className="capability-icon">
                            <i className="dx-icon-clock"></i>
                        </div>
                        <h3>Teslimat Takibi</h3>
                        <p>Gerçek zamanlı teslimat durumu</p>
                    </div>
                    <div className="capability-card">
                        <div className="capability-icon">
                            <i className="dx-icon-globe"></i>
                        </div>
                        <h3>GPS Takip</h3>
                        <p>Anlık konum ve rota izleme</p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features-section">
                <div className="section-header">
                    <span className="section-badge">
                        <i className="dx-icon-preferences"></i>
                        Neden MobiRoute?
                    </span>
                    <h2 className="section-title">Teslimatlarınızı Dönüştüren Özellikler</h2>
                    <p className="section-subtitle">
                        Gelişmiş özelliklerimizle operasyonlarınızı optimize edin
                    </p>
                </div>

                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <div className={`feature-icon ${feature.color}`}>
                                <i className={feature.icon}></i>
                            </div>
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Products Section */}
            <section id="products" className="products-section">
                <div className="section-header">
                    <span className="section-badge">
                        <i className="dx-icon-box"></i>
                        Ürünlerimiz
                    </span>
                    <h2 className="section-title">Filonuzu Gerçek Zamanlı Takip Edin</h2>
                    <p className="section-subtitle">
                        Etkili rota planlama ile zaman kazanın
                    </p>
                </div>

                <div className="product-tabs">
                    <button
                        className={`product-tab ${activeTab === 'web' ? 'active' : ''}`}
                        onClick={() => setActiveTab('web')}
                    >
                        <i className="dx-icon-desktop"></i>
                        Web - Filo Yöneticisi
                    </button>
                    <button
                        className={`product-tab ${activeTab === 'mobile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('mobile')}
                    >
                        <i className="dx-icon-tel"></i>
                        Mobil - Rota Planlayıcı
                    </button>
                </div>

                <div className="product-content">
                    {activeTab === 'web' ? (
                        <div className="product-details">
                            <div className="product-info">
                                <h3>Filo Yönetim Paneli</h3>
                                <p>Tüm sürücülerinizi yönetmek için uçtan uca çözüm - rotalar oluşturun, teslimat ilerlemesini takip edin, canlı konumu görüntüleyin ve müşterilerinizi bilgilendirin.</p>
                                <ul className="product-features">
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Rotalarınızı önceden planlayın
                                    </li>
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Teslimat durumunu anlık takip edin
                                    </li>
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Depo konumunuzdan rotaları başlatın
                                    </li>
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Sadece kullandığınız kadar ödeyin
                                    </li>
                                </ul>
                            </div>
                            <div className="product-visual">
                                <div className="product-mockup web">
                                    <div className="mockup-header">
                                        <div className="mockup-dots">
                                            <span></span><span></span><span></span>
                                        </div>
                                        <div className="mockup-title">MobiRoute Dashboard</div>
                                    </div>
                                    <div className="mockup-content">
                                        <div className="mockup-sidebar">
                                            <div className="mockup-menu-item active"></div>
                                            <div className="mockup-menu-item"></div>
                                            <div className="mockup-menu-item"></div>
                                            <div className="mockup-menu-item"></div>
                                        </div>
                                        <div className="mockup-main">
                                            <div className="mockup-map"></div>
                                            <div className="mockup-stats">
                                                <div className="mockup-stat"></div>
                                                <div className="mockup-stat"></div>
                                                <div className="mockup-stat"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="product-details">
                            <div className="product-info">
                                <h3>Mobil Rota Planlayıcı</h3>
                                <p>MobiRoute mobil uygulamasıyla daha hızlı teslimat yapın ve rotalarınızı daha iyi planlayın - önceden rotalarınızı oluşturun, teslimat kanıtı ekleyin ve müşterilerinizle canlı güncellemeler paylaşın.</p>
                                <ul className="product-features">
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Müşterilere özel mesajlar gönderin
                                    </li>
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Teslimat kanıtı toplayın
                                    </li>
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Rotalarınızı önceden planlayın
                                    </li>
                                    <li>
                                        <i className="dx-icon-check"></i>
                                        Canlı konum paylaşın
                                    </li>
                                </ul>
                            </div>
                            <div className="product-visual">
                                <div className="product-mockup mobile">
                                    <div className="phone-frame">
                                        <div className="phone-notch"></div>
                                        <div className="phone-screen">
                                            <div className="phone-header">
                                                <span>MobiRoute</span>
                                            </div>
                                            <div className="phone-map"></div>
                                            <div className="phone-route">
                                                <div className="route-item"></div>
                                                <div className="route-item"></div>
                                                <div className="route-item"></div>
                                            </div>
                                            <div className="phone-nav">
                                                <div className="nav-item active"></div>
                                                <div className="nav-item"></div>
                                                <div className="nav-item"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className="testimonials-section">
                <div className="section-header">
                    <span className="section-badge">
                        <i className="dx-icon-comment"></i>
                        Referanslar
                    </span>
                    <h2 className="section-title">Mutlu Müşterilerimizden</h2>
                    <p className="section-subtitle">
                        MobiRoute ile teslimatlarını dönüştüren şirketlerin yorumları
                    </p>
                </div>

                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <div key={index} className="testimonial-card">
                            <div className="testimonial-quote">
                                <i className="dx-icon-comment"></i>
                            </div>
                            <p className="testimonial-text">"{testimonial.text}"</p>
                            <div className="testimonial-footer">
                                <div className="testimonial-avatar">{testimonial.avatar}</div>
                                <div className="testimonial-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.role} - {testimonial.company}</p>
                                </div>
                            </div>
                            <div className="testimonial-rating">
                                {[...Array(5)].map((_, i) => (
                                    <i key={i} className="dx-icon-favorites"></i>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="faq-section">
                <div className="section-header">
                    <span className="section-badge">
                        <i className="dx-icon-help"></i>
                        SSS
                    </span>
                    <h2 className="section-title">Sıkça Sorulan Sorular</h2>
                    <p className="section-subtitle">
                        MobiRoute hakkında merak edilenler
                    </p>
                </div>

                <div className="faq-list">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                            onClick={() => toggleFaq(index)}
                        >
                            <div className="faq-question">
                                <h4>{faq.question}</h4>
                                <i className="dx-icon-chevrondown"></i>
                            </div>
                            <div className="faq-answer">
                                <p>{faq.answer}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="cta-decorations">
                    <div className="cta-circle circle-1"></div>
                    <div className="cta-circle circle-2"></div>
                </div>
                <div className="cta-content">
                    <h2>Rotalarınızı Optimize Etmeye Başlayın</h2>
                    <p>10,000+ işletme ve sürücü sorunsuz filo yönetimi için bize güveniyor.</p>
                    <div className="cta-buttons">
                        <button className="btn-cta" onClick={handleSignIn}>
                            Giriş Yap
                            <i className="dx-icon-arrowright"></i>
                        </button>
                        <button className="btn-cta-outline" onClick={() => scrollToSection('features')}>
                            Daha Fazla Bilgi
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="welcome-footer">
                <div className="footer-main">
                    <div className="footer-brand">
                        <img src={logo} alt="MobiRoute" className="footer-logo" />
                        <p className="footer-desc">
                            Fores GmbH'nin kurumsal lojistik çözümü. Filo yönetimi, rota optimizasyonu ve saha ekibi takibi.
                        </p>
                        <div className="footer-contact">
                            <p><i className="dx-icon-email"></i> info@fores.de</p>
                            <p><i className="dx-icon-tel"></i> +49 123 456 789</p>
                        </div>
                    </div>
                    <div className="footer-links">
                        <div className="footer-column">
                            <h4>Ürünler</h4>
                            <button onClick={() => scrollToSection('products')}>Filo Yöneticisi</button>
                            <button onClick={() => scrollToSection('products')}>Mobil Uygulama</button>
                            <button onClick={() => scrollToSection('features')}>Özellikler</button>
                        </div>
                        <div className="footer-column">
                            <h4>Destek</h4>
                            <button onClick={() => scrollToSection('faq')}>SSS</button>
                            <button onClick={() => scrollToSection('testimonials')}>Referanslar</button>
                            <button onClick={handleSignIn}>İletişim</button>
                        </div>
                        <div className="footer-column">
                            <h4>Şirket</h4>
                            <a href="https://fores.de" target="_blank" rel="noopener noreferrer">Hakkımızda</a>
                            <a href="https://fores.de" target="_blank" rel="noopener noreferrer">Fores GmbH</a>
                        </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© 2025 <a href="https://fores.de" target="_blank" rel="noopener noreferrer">Fores GmbH</a>. Tüm hakları saklıdır.</p>
                    <div className="footer-powered">
                        <span>Powered by</span>
                        <img src={foresLogo} alt="Fores" className="powered-logo" />
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default WelcomePage;
